// src/pages/Contact.jsx
import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';

function EmailIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1840b5" strokeWidth="1.5">
      <rect x="4" y="7" width="24" height="18" rx="2"/>
      <polyline points="4,7 16,17 28,7"/>
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1840b5" strokeWidth="1.5">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.11 18a19.5 19.5 0 0 1-3-3.22 19.79 19.79 0 0 1-3.88-8.6A2 2 0 0 1 6.22 4h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L10.09 11.9a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 24 18z"/>
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1840b5" strokeWidth="1.5">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

const INQUIRY_TYPES = [
  'Government / RFP',
  'Managed IT Services',
  'Workforce Staffing',
  'Training Program',
  'Partnership',
  'General Inquiry',
];

function Toast({ message, type, onClose }) {
  return (
    <div className={`toast toast--${type}`}>
      <span>{message}</span>
      <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#686779' }}>×</button>
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    inquiryType: 'Government / RFP',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, 'contact_submissions'), {
        ...form,
        createdAt: serverTimestamp(),
        source: 'website',
      });
      setToast({ message: "Your message has been sent! We'll get back to you shortly.", type: 'success' });
      setForm({ name: '', email: '', organization: '', inquiryType: 'Government / RFP', message: '' });
    } catch (err) {
      console.error(err);
      setToast({ message: 'Something went wrong. Please try again or email us directly.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Us"
        description="Contact District Sourcing Tech for procurement inquiries, service discussions and workforce-related communication. Email: admin@districtsourcingtech.com | Phone: +1-571-758-0720"
        url="https://districtsourcingtech.com/contact"
        keywords="contact District Sourcing Tech, government IT contact, workforce staffing inquiry, IT services contact"
      />

      <Navbar variant="light" />

      <section className="section">
        <div className="contact-layout" style={{ maxWidth: '1376px', margin: '0 auto' }}>
          {/* Left: Contact info */}
          <Reveal className="contact-info">
            <div>
              <h1 className="title-1" style={{ marginBottom: '8px' }}>Contact us</h1>
              <p className="body-lg" style={{ color: 'var(--token-desc)' }}>
                Contact us for procurement inquiries service discussions and workforce-related communication.
              </p>
            </div>

            <div className="contact-details">
              <div className="contact-row">
                <Reveal className="contact-item">
                  <div className="contact-item__icon"><EmailIcon /></div>
                  <div>
                    <p className="title-6 contact-item__label">Email</p>
                    <a href="mailto:admin@districtsourcingtech.com" className="contact-item__value" style={{ textDecoration: 'none' }}>
                      admin@districtsourcingtech.com
                    </a>
                  </div>
                </Reveal>
                <Reveal delay={100} className="contact-item">
                  <div className="contact-item__icon"><PhoneIcon /></div>
                  <div>
                    <p className="title-6 contact-item__label">Phone</p>
                    <a href="tel:+15717580720" className="contact-item__value" style={{ textDecoration: 'none' }}>
                      +1-571-758-0720
                    </a>
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="contact-item" style={{ maxWidth: '240px' }}>
                <div className="contact-item__icon"><MapPinIcon /></div>
                <div>
                  <p className="title-6 contact-item__label">Office</p>
                  <address className="contact-item__value" style={{ fontStyle: 'normal' }}>
                    6560 Backlick Rd<br />
                    Springfield, VA 22150
                  </address>
                </div>
              </Reveal>
            </div>
          </Reveal>

          {/* Right: Form */}
          <Reveal delay={150} className="contact-form-wrap">
            <form className="form-card" onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label className="form-label" htmlFor="name">
                  Name <span className="required">*</span>
                </label>
                <input
                  id="name" name="name" type="text" className="form-input"
                  placeholder="Full name" value={form.name} onChange={handleChange} required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="email">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    id="email" name="email" type="email" className="form-input"
                    placeholder="Email address" value={form.email} onChange={handleChange} required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="organization">
                    Organization <span className="required">*</span>
                  </label>
                  <input
                    id="organization" name="organization" type="text" className="form-input"
                    placeholder="Organization" value={form.organization} onChange={handleChange} required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="inquiryType">
                  Inquiry type <span className="required">*</span>
                </label>
                <div className="form-select-wrap">
                  <select
                    id="inquiryType" name="inquiryType" className="form-select"
                    value={form.inquiryType} onChange={handleChange} required
                  >
                    {INQUIRY_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="message">Message</label>
                <textarea
                  id="message" name="message" className="form-textarea"
                  placeholder="Type your message..." value={form.message} onChange={handleChange}
                />
              </div>

              <button
                type="submit" className="btn btn-primary btn-full" disabled={loading}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
              >
                {loading && <span className="spinner" />}
                {loading ? 'Sending...' : 'Submit'}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <Footer />

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </>
  );
}
